/**
 * Debug Logging Utility for RestoreLink CRM
 *
 * Provides consistent logging across the application with:
 * - Environment-aware logging (verbose in development)
 * - Structured log format with timestamps
 * - Error context capture
 * - Performance timing
 * - Log storage for diagnostics dashboard
 */

const isDev = import.meta.env.DEV;
const DEBUG_PREFIX = '[RestoreLink]';
const MAX_STORED_LOGS = 500;
const LOG_STORAGE_KEY = 'restorelink_logs';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type Severity = 'low' | 'medium' | 'high' | 'critical';

export interface StoredLog {
  id: string;
  timestamp: string;
  level: LogLevel;
  severity: Severity;
  module: string;
  action: string;
  message?: string;
  stack?: string;
  data?: Record<string, unknown>;
  duration?: number;
  source: 'pwa';
}

interface LogContext {
  module: string;
  action: string;
  userId?: string;
  data?: Record<string, unknown>;
  duration?: number;
  error?: Error;
}

// In-memory log buffer
const logBuffer: StoredLog[] = [];

/**
 * Map log level to severity
 */
function levelToSeverity(level: LogLevel): Severity {
  switch (level) {
    case 'debug':
      return 'low';
    case 'info':
      return 'low';
    case 'warn':
      return 'medium';
    case 'error':
      return 'high';
    default:
      return 'low';
  }
}

/**
 * Generate unique log ID
 */
function generateLogId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Store log entry
 */
function storeLog(entry: StoredLog): void {
  logBuffer.unshift(entry);

  // Trim buffer if too large
  if (logBuffer.length > MAX_STORED_LOGS) {
    logBuffer.length = MAX_STORED_LOGS;
  }

  // Persist to localStorage (debounced in production)
  try {
    const logsToStore = logBuffer.slice(0, MAX_STORED_LOGS);
    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(logsToStore));
  } catch {
    // Storage full or unavailable - silently ignore
  }
}

/**
 * Load stored logs from localStorage
 */
export function loadStoredLogs(): StoredLog[] {
  try {
    const stored = localStorage.getItem(LOG_STORAGE_KEY);
    if (stored) {
      const logs = JSON.parse(stored) as StoredLog[];
      // Restore to buffer
      logBuffer.length = 0;
      logBuffer.push(...logs);
      return logs;
    }
  } catch {
    // Parse error - return empty
  }
  return [];
}

/**
 * Get logs from memory buffer
 */
export function getLogs(): StoredLog[] {
  return [...logBuffer];
}

/**
 * Get logs filtered by severity
 */
export function getLogsBySeverity(minSeverity: Severity): StoredLog[] {
  const severityOrder: Severity[] = ['low', 'medium', 'high', 'critical'];
  const minIndex = severityOrder.indexOf(minSeverity);

  return logBuffer.filter(log => {
    const logIndex = severityOrder.indexOf(log.severity);
    return logIndex >= minIndex;
  });
}

/**
 * Get logs filtered by level
 */
export function getLogsByLevel(levels: LogLevel[]): StoredLog[] {
  return logBuffer.filter(log => levels.includes(log.level));
}

/**
 * Get error logs only
 */
export function getErrorLogs(): StoredLog[] {
  return logBuffer.filter(log => log.level === 'error' || log.level === 'warn');
}

/**
 * Clear all stored logs
 */
export function clearLogs(): void {
  logBuffer.length = 0;
  try {
    localStorage.removeItem(LOG_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Format log message with consistent structure
 */
function formatMessage(level: LogLevel, ctx: LogContext): string {
  const timestamp = new Date().toISOString();
  return `${DEBUG_PREFIX} [${timestamp}] [${level.toUpperCase()}] [${ctx.module}] ${ctx.action}`;
}

/**
 * Create and store a log entry
 */
function createLogEntry(
  level: LogLevel,
  module: string,
  action: string,
  data?: Record<string, unknown>,
  error?: Error,
  duration?: number
): StoredLog {
  const entry: StoredLog = {
    id: generateLogId(),
    timestamp: new Date().toISOString(),
    level,
    severity: error ? 'high' : levelToSeverity(level),
    module,
    action,
    data,
    duration,
    source: 'pwa',
  };

  if (error) {
    entry.message = error.message;
    entry.stack = error.stack;
    // Upgrade severity for critical errors
    if (error.message.includes('auth') || error.message.includes('permission')) {
      entry.severity = 'critical';
    }
  }

  return entry;
}

/**
 * Main logger object
 */
export const logger = {
  /**
   * Debug level - only logs in development
   */
  debug(module: string, action: string, data?: Record<string, unknown>) {
    if (isDev) {
      console.log(formatMessage('debug', { module, action }), data || '');
    }
    // Store debug logs only in dev
    if (isDev) {
      storeLog(createLogEntry('debug', module, action, data));
    }
  },

  /**
   * Info level - general operational logs
   */
  info(module: string, action: string, data?: Record<string, unknown>) {
    console.log(formatMessage('info', { module, action }), data || '');
    storeLog(createLogEntry('info', module, action, data));
  },

  /**
   * Warning level - potential issues
   */
  warn(module: string, action: string, data?: Record<string, unknown>) {
    console.warn(formatMessage('warn', { module, action }), data || '');
    storeLog(createLogEntry('warn', module, action, data));
  },

  /**
   * Error level - failures and exceptions
   */
  error(module: string, action: string, error: unknown, data?: Record<string, unknown>) {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    console.error(formatMessage('error', { module, action }), {
      message: errorObj.message,
      stack: errorObj.stack,
      ...data,
    });
    storeLog(createLogEntry('error', module, action, data, errorObj));
  },

  /**
   * Log API/Firestore operation with timing
   */
  async operation<T>(
    module: string,
    action: string,
    fn: () => Promise<T>,
    data?: Record<string, unknown>
  ): Promise<T> {
    const start = performance.now();

    if (isDev) {
      console.log(formatMessage('debug', { module, action: `${action} - START` }), data || '');
    }

    try {
      const result = await fn();
      const duration = Math.round(performance.now() - start);

      if (isDev) {
        console.log(formatMessage('debug', { module, action: `${action} - SUCCESS (${duration}ms)` }));
      }

      // Store successful operations with timing
      storeLog(createLogEntry('info', module, `${action} - SUCCESS`, { ...data, duration }, undefined, duration));

      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);
      const errorObj = error instanceof Error ? error : new Error(String(error));

      console.error(formatMessage('error', { module, action: `${action} - FAILED (${duration}ms)` }), {
        message: errorObj.message,
        stack: errorObj.stack,
        ...data,
      });

      storeLog(createLogEntry('error', module, `${action} - FAILED`, { ...data, duration }, errorObj, duration));

      throw error;
    }
  },

  /**
   * Log Firebase Auth operations
   */
  auth(action: string, data?: Record<string, unknown>) {
    this.info('Auth', action, data);
  },

  /**
   * Log Firestore operations
   */
  firestore(action: string, collection: string, data?: Record<string, unknown>) {
    this.debug('Firestore', `${action} on ${collection}`, data);
  },

  /**
   * Log Cloud Function calls
   */
  functions(functionName: string, action: string, data?: Record<string, unknown>) {
    this.debug('Functions', `${functionName}: ${action}`, data);
  },

  /**
   * Log navigation/routing
   */
  navigation(action: string, data?: Record<string, unknown>) {
    this.debug('Navigation', action, data);
  },

  /**
   * Log user actions for audit trail
   */
  userAction(action: string, userId: string, data?: Record<string, unknown>) {
    this.info('UserAction', action, { userId, ...data });
  },
};

/**
 * Create a module-specific logger
 */
export function createLogger(module: string) {
  return {
    debug: (action: string, data?: Record<string, unknown>) => logger.debug(module, action, data),
    info: (action: string, data?: Record<string, unknown>) => logger.info(module, action, data),
    warn: (action: string, data?: Record<string, unknown>) => logger.warn(module, action, data),
    error: (action: string, error: unknown, data?: Record<string, unknown>) =>
      logger.error(module, action, error, data),
    operation: <T>(action: string, fn: () => Promise<T>, data?: Record<string, unknown>) =>
      logger.operation(module, action, fn, data),
  };
}

// Load stored logs on module init
loadStoredLogs();

export default logger;
